import { CantileverButton } from 'components/CantileverButton'
import { GeckoNotificationProvider } from 'features/notifications'
import {
  ActiveTimeRangeSelector,
  ScatterplotProvider,
  ScatterplotSidebar,
  TimeSeriesScatterplotContent,
  useScatterplotContext,
} from 'features/timeSeries'
import { DashboardHeader } from 'features/timeSeries/components/DashboardHeader'
import { ScatterplotSidebarView } from 'features/timeSeries/components/ScatterplotSidebar'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { PanelLeftOpen } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const ScatterplotAndSidebarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  overflow: visible;
  position: relative;
`

const ChartWithFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const ChartFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${CHART_COLORS.BACKGROUND};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

const SidebarToggleButton = styled(CantileverButton)`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 1;
`

interface TimeSeriesScatterplotProps {
  chartID: string
  siteID: string
  showToolbar?: boolean
  showChartSettings?: boolean
  chartAreaRef?: React.RefObject<HTMLDivElement>
  unitID?: string
  unitName?: string
  children?: React.ReactNode
  chartContainer?: React.ComponentType<{ children: React.ReactNode }>
  disableSaving?: boolean
  hideSidebar?: boolean
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
  hideHeader?: boolean
  hideFooter?: boolean
  toggleChatbot?: () => void
}

interface TimeSeriesScatterplotInnerProps {
  showToolbar?: boolean
  showChartSettings?: boolean
  chartAreaRef?: React.RefObject<HTMLDivElement>
  unitID?: string
  unitName?: string
  children?: React.ReactNode
  chartContainer?: React.ComponentType<{ children: React.ReactNode }>
  disableSaving?: boolean
  hideSidebar?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
  toggleChatbot?: () => void
}

const TimeSeriesScatterplotInner: React.FC<
  TimeSeriesScatterplotInnerProps & {
    sidebarView: ScatterplotSidebarView
    setSidebarView: React.Dispatch<React.SetStateAction<ScatterplotSidebarView>>
  }
> = ({
  showChartSettings = true,
  chartAreaRef,
  unitID,
  unitName,
  children,
  chartContainer: ChartContainerComponent,
  sidebarView,
  setSidebarView,
  hideSidebar = false,
  hideHeader = false,
  hideFooter = false,
  toggleChatbot,
}) => {
  const { sensorEntries, selectedSensors, timeRange, setTimeRange, liveTail } =
    useScatterplotContext()
  const [scatterplotSidebarOpen, setScatterplotSidebarOpen] =
    useState(!hideSidebar)

  // Force chart resize when sidebar view changes
  useEffect(() => {
    // Small delay to ensure the DOM has updated
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)

    return () => clearTimeout(timer)
  }, [sidebarView])

  return (
    <>
      {children}
      {ChartContainerComponent ? (
        <ChartContainerComponent>
          <MainContainer>
            {!hideHeader && <DashboardHeader toggleChatbot={toggleChatbot} />}
            <ScatterplotAndSidebarsContainer>
              {!hideSidebar && (
                <>
                  {scatterplotSidebarOpen ? (
                    <ScatterplotSidebar
                      onClose={() => setScatterplotSidebarOpen(false)}
                      tagsOptions={sensorEntries}
                      selectedTags={selectedSensors}
                      setSidebarView={setSidebarView}
                      sidebarView={sidebarView}
                    />
                  ) : (
                    <SidebarToggleButton
                      iconOnly
                      ghost
                      onClick={() => setScatterplotSidebarOpen(true)}>
                      <PanelLeftOpen width={'1.25rem'} height={'1.25rem'} />
                    </SidebarToggleButton>
                  )}
                </>
              )}
              <ChartWithFooterWrapper>
                <TimeSeriesScatterplotContent
                  ref={chartAreaRef}
                  showChartSettings={showChartSettings}
                  unitID={unitID}
                  unitName={unitName}
                  sidebarView={sidebarView}
                />
                {!hideFooter && (
                  <ChartFooter>
                    <ActiveTimeRangeSelector
                      timeRange={timeRange}
                      setTimeRange={setTimeRange}
                      liveTail={liveTail}
                    />
                  </ChartFooter>
                )}
              </ChartWithFooterWrapper>
            </ScatterplotAndSidebarsContainer>
          </MainContainer>
        </ChartContainerComponent>
      ) : (
        <MainContainer>
          {!hideHeader && <DashboardHeader />}
          <TimeSeriesScatterplotContent
            ref={chartAreaRef}
            showChartSettings={showChartSettings}
            unitID={unitID}
            unitName={unitName}
            sidebarView={sidebarView}
          />
        </MainContainer>
      )}
    </>
  )
}

export const TimeSeriesScatterplot: React.FC<TimeSeriesScatterplotProps> = ({
  chartID,
  siteID,
  disableSaving,
  hideSidebar = false,
  onChartSaved,
  hideHeader = false,
  hideFooter = false,
  ...props
}) => {
  const [sidebarView, setSidebarView] = useState<ScatterplotSidebarView>(
    ScatterplotSidebarView.LEGEND
  )

  return (
    <GeckoNotificationProvider>
      <ScatterplotProvider
        chartID={chartID}
        siteID={siteID}
        disableSaving={disableSaving}
        onChartSaved={onChartSaved}>
        <TimeSeriesScatterplotInner
          {...props}
          sidebarView={sidebarView}
          setSidebarView={setSidebarView}
          hideSidebar={hideSidebar}
          hideHeader={hideHeader}
          hideFooter={hideFooter}
        />
      </ScatterplotProvider>
    </GeckoNotificationProvider>
  )
}
